import { modalController } from '@ionic/vue';
import StepUpCodeModal from '@/components/Auth/StepUpCodeModal.vue';

/**
 * Prompt for admin step-up OTP. Returns { challengeId, otp } or null if cancelled.
 * @param {{ challengeId: number|string, onResend?: (challengeId) => Promise<number|string> }} options
 */
export async function promptStepUpCode({ challengeId, onResend } = {}) {
  const modal = await modalController.create({
    component: StepUpCodeModal,
    componentProps: {
      challengeId,
      onResend,
    },
    backdropDismiss: false,
  });
  await modal.present();
  const { data, role } = await modal.onDidDismiss();
  if (role === 'backdrop' || !data?.otp) return null;
  return data;
}

/**
 * Run an API request; on HTTP 428 with requiresStepUp, prompt for OTP and retry once.
 * @param {(stepUp?: { challengeId: number|string, otp?: string }) => Promise<Response&{data?:any}>} makeRequest
 */
export async function withStepUp(makeRequest) {
  let response = await makeRequest();
  if (response.status !== 428 || !response.data?.requiresStepUp) {
    return response;
  }

  let challengeId = response.data.challengeId;
  const result = await promptStepUpCode({
    challengeId,
    onResend: async (currentId) => {
      const resendResponse = await makeRequest({ challengeId: currentId });
      if (resendResponse.status === 428 && resendResponse.data?.challengeId) {
        challengeId = resendResponse.data.challengeId;
        return challengeId;
      }
      if (!resendResponse.ok) {
        const message = resendResponse.data?.message
          || resendResponse.data?.error
          || 'Erneutes Senden fehlgeschlagen';
        throw new Error(message);
      }
      return currentId;
    },
  });

  if (!result?.otp) {
    return response;
  }

  return makeRequest({
    challengeId: result.challengeId || challengeId,
    otp: result.otp,
  });
}

export const stepUpHeaders = (stepUp) => {
  if (!stepUp?.challengeId) return {};
  const headers = {
    'X-Admin-Step-Up-Challenge': String(stepUp.challengeId),
  };
  if (stepUp.otp) {
    headers['X-Admin-Step-Up-Code'] = String(stepUp.otp);
  }
  return headers;
};
